import { getChordNotes, parseChord, splitInversion } from './chordNotes';
import { getChromaticScale } from './diatonicChords';

/**
 * Where a chord's notes actually sit, rather than which notes it is made of.
 *
 * chordNotes.js answers what a symbol means; this answers what a hand does with it. A chord written
 * on a chart says nothing about its voicing, and the app's answer until now was the only one that
 * needs no thought: stack it from the root, every time. That is why a played chart leaps home at
 * every change — a player keeps the hand where it is and moves the fewest notes.
 *
 * Deliberately a leaf, like chordRhythm.js: it reads intervals and hands back MIDI numbers, and
 * knows nothing about bars, tempo or the clip shape. The octave to voice from is passed in for the
 * same reason it is passed into getRhythmBassPair — chordBarsMidi.js owns that decision and imports
 * this, so this can't import that.
 */

/** A note name's pitch class, 0 for C. Both chromatic scales start at C, so the index is it. */
export const getPitchClass = (note) => {
  const index = getChromaticScale(note).indexOf(note);

  return index === -1 ? null : index;
};

/**
 * Whether a chord is one that inverts at all.
 *
 * An interval of 12 or more is a note CHORD_INTERVALS deliberately put in the second octave — the 9
 * of an add9, the 11 of an 11. Those are already spread voicings, chosen for the sound of the gap,
 * and rotating one closes that gap into a cluster: Cadd9 inverted is a C, D, E and G inside a single
 * octave, which is a different and worse chord. Triads, sevenths and sixths are what invert.
 */
const isInvertible = (intervals) => intervals.every((interval) => interval < 12);

/** The chord stacked up from its root, which is where every voicing starts before it is moved. */
const rootPosition = (intervals, rootClass, rootOctaveMidi) =>
  intervals.map((interval) => rootOctaveMidi + rootClass + interval);

/**
 * The intervals a chord actually sounds at, once its inversion mark is read: the lowest few notes
 * taken up an octave, so an inversion is the same notes with the bottom of the stack moved to the
 * top. Still measured from the root, which is what everything drawing a chord counts from.
 *
 * The single answer to what an inversion *is* — the voicing below and the keyboard in ChordPiano
 * both read it here, so what is heard and what is drawn cannot drift apart.
 *
 * A chord that doesn't invert, or a mark asking for more than there is to move, comes back as far
 * over as it goes rather than wrapping round to the root.
 */
export const voicedIntervals = (intervals, inversion = 0) => {
  if (!isInvertible(intervals)) return intervals;

  const k = Math.min(Math.max(0, inversion), intervals.length - 1);

  return intervals.map((interval, i) => (i < k ? interval + 12 : interval)).sort((a, b) => a - b);
};

/**
 * The same voicing moved by whole octaves until its lowest note is in the octave chords are voiced
 * from. Without it, a run of nearest-shape choices walks off the top of the chart — each change is
 * a small step, and enough small steps in one direction leave the register the samples are good in.
 */
const inRegister = (notes, rootOctaveMidi) => {
  const shift = Math.floor((notes[0] - rootOctaveMidi) / 12) * 12;

  return shift === 0 ? notes : notes.map((midi) => midi - shift);
};

/** Where a voicing sits, as one number — what "near the last chord" is measured on. */
const centre = (notes) => notes.reduce((total, midi) => total + midi, 0) / notes.length;

/**
 * One chord's upper body, voiced.
 *
 * Three answers, in this order:
 *
 * 1. A chord that doesn't invert comes back in root position, exactly as it always did.
 * 2. A written inversion is that inversion, whether or not anything asked for voice leading — the
 *    mark is somebody saying outright how they want it played. More marks than the chord has notes
 *    to move is played as far over as it goes rather than wrapping back to the root.
 * 3. Given the voicing before it, the inversion nearest that one; otherwise root position.
 *
 * Nothing here looks at a slash chord's bass. That note is not part of the body — it sits under it,
 * which is chordBarsMidi.js's business.
 *
 * @param {number[]} intervals - Semitone offsets from the root, as chordNotes.js gives them
 * @param {number} rootClass - Pitch class of the root, 0 for C
 * @param {number} rootOctaveMidi - MIDI number of the C the chord is voiced from
 * @param {object} options - { inversion, previous }
 * @returns {number[]} MIDI numbers, ascending
 */
export const voiceChordBody = (intervals, rootClass, rootOctaveMidi, { inversion = 0, previous = null } = {}) => {
  const notes = rootPosition(intervals, rootClass, rootOctaveMidi);

  if (!isInvertible(intervals)) return notes;

  const candidates = intervals.map((_, k) =>
    inRegister(rootPosition(voicedIntervals(intervals, k), rootClass, rootOctaveMidi), rootOctaveMidi),
  );

  if (inversion > 0) return candidates[Math.min(inversion, candidates.length - 1)];

  if (!previous || previous.length === 0) return candidates[0];

  const target = centre(previous);

  // Every candidate is already in the same octave, so this is a choice between inversions and not
  // between registers — which is what keeps it from drifting however long the chart runs.
  return candidates.reduce((best, voicing) =>
    Math.abs(centre(voicing) - target) < Math.abs(centre(best) - target) ? voicing : best,
  );
};

/**
 * The symbols one chord can be written as, root position first: ['C', 'C^1', 'C^2'].
 *
 * Marking an inversion in the chart is a rename and nothing more, which is the whole reason the
 * mark hangs off the end of the symbol: the root, the quality and any bass note are untouched by
 * it, so everything that reads those goes on reading them.
 *
 * Empty for anything there is no cycle for — a symbol the parser can't read, or a chord that spans
 * too far to invert.
 */
export const inversionChords = (chord) => {
  const { symbol } = splitInversion(String(chord ?? '').trim());

  if (!parseChord(symbol)) return [];

  const { intervals } = getChordNotes(symbol);

  if (!isInvertible(intervals)) return [];

  return intervals.map((_, i) => (i === 0 ? symbol : `${symbol}^${i}`));
};

/** Which of those a chord is written as now — 0 for root position, -1 when it has no cycle. */
export const inversionIndex = (chord) => inversionChords(chord).indexOf(String(chord ?? '').trim());

/** The next inversion round the cycle — what tapping the chord menu's Voicing row writes. */
export const nextInversionChord = (chord) => {
  const chords = inversionChords(chord);

  if (chords.length === 0) return null;

  return chords[(Math.max(0, inversionIndex(chord)) + 1) % chords.length];
};
